import React, { useState, useEffect } from "react";
import api from "../api"; 
import { toast } from 'react-toastify'; 

const ApproveBeneficiaryListPage = ({ role, projectId: propProjectId, departmentId: propDeptId }) => {
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [selected, setSelected] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    // --- AUTO-DETECT PROJECT LOGIC ---
    // This helper function tries to find the default project from the stored user data
    const getDefaultProjectDetails = () => {
        try {
            // Check for 'currentUser' or 'user' in local storage
            const storedUser = localStorage.getItem("currentUser") || localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                // If the user has a projects list, return the first one as default
                if (user.projects && user.projects.length > 0) {
                    return {
                        projectId: user.projects[0].projectId,
                        departmentId: user.projects[0].departmentId
                    };
                }
            }
        } catch (err) {
            console.error("Error parsing user data for defaults", err);
        }
        return {};
    };

    const defaults = getDefaultProjectDetails();

    // FINAL ID RESOLUTION:
    // 1. Use the prop if passed (Highest priority)
    // 2. Use specific localStorage keys (if your app sets them individually)
    // 3. Use the default project found in the currentUser object (Fallback)
    const projectId = propProjectId || localStorage.getItem("selectedProjectId") || defaults.projectId;
    const departmentId = propDeptId || localStorage.getItem("departmentId") || defaults.departmentId;

    useEffect(() => {
        const fetchBeneficiaries = async () => {
            if (!projectId) {
                setError("No project found. Please ensure you are assigned to a project.");
                setBeneficiaries([]);
                return;
            }
             if (!departmentId) {
                setError("Department ID not found for this project.");
                setBeneficiaries([]);
                return;
            }

            setIsLoading(true);
            setError("");
            setMessage(""); 
            try {
                const response = await api.get(
                    `/vres/projects/${projectId}/departments/${departmentId}/beneficiaries?status=pending_approval`
                );
                setBeneficiaries(response.data || []); 
            } catch (err) {
                const errorMessage =
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to fetch beneficiaries.";
                if (err.response?.status === 403) {
                    setError("You are not authorized to view this page. Please log in again.");
                } else {
                    setError(errorMessage);
                }
                setBeneficiaries([]); 
            } finally {
                setIsLoading(false);
            }
        };

        fetchBeneficiaries();
    }, [projectId, departmentId]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = beneficiaries.map(b => b.beneficiaryId);
            setSelected(allIds);
        } else {
            setSelected([]);
        }
    };

    const handleCheckboxChange = (beneficiaryId) => {
        setSelected((prev) =>
            prev.includes(beneficiaryId)
                ? prev.filter((id) => id !== beneficiaryId)
                : [...prev, beneficiaryId]
        );
    };

    const handleStatusUpdate = async (newStatus) => {
        if (selected.length === 0) {
            const selectErrorMsg = `No beneficiaries selected to ${newStatus}.`;
            setError(selectErrorMsg);
            toast.error(selectErrorMsg); 
            return;
        }

        setIsLoading(true);
        setMessage("");
        setError("");

        try {
            const response = await api.put(
                "/vres/beneficiaries/status", 
                {
                    beneficiaryIds: selected,
                    status: newStatus,
                }
            );

            const successMsg = response.data.message || `Successfully updated status for ${selected.length} beneficiaries.`;
            setMessage(successMsg);
            toast.success(successMsg); 

            setBeneficiaries(
                beneficiaries.filter((b) => !selected.includes(b.beneficiaryId))
            );
            setSelected([]); 
        } catch (err) {
            console.error("Status update error:", err); 

            let displayMessage = `Failed to ${newStatus} beneficiaries. Please try again.`; 

            if (err.response && err.response.data) {
                const backendData = err.response.data;
                let backendMessage = "";

                 if (backendData && typeof backendData === 'object' && backendData.message) {
                    backendMessage = backendData.message;
                } else if (typeof backendData === 'string') {
                    backendMessage = backendData;
                }

                if (backendMessage) {
                    displayMessage = `Update failed: ${backendMessage}`;
                } else if (err.response.status === 403) {
                     displayMessage = "Update failed: You do not have permission.";
                } else {
                    displayMessage = `Update failed: Request failed with status code ${err.response.status}.`;
                }
            } else if (err.message) {
                displayMessage = `Update failed: ${err.message}`;
            }

            setError(displayMessage); 
            toast.error(displayMessage); 

        } finally {
            setIsLoading(false);
        }
    };

    const canApproveReject = role === "checker"; 
    const isAllSelected = beneficiaries.length > 0 && selected.length === beneficiaries.length;

    // --- STYLES INJECTION ---
    const PageStyleBlock = () => (
        <style>{`
            .ab-checkbox {
                cursor: pointer;
                width: 16px;
                height: 16px;
                accent-color: #20c997; 
            }
            .ab-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
            .ab-btn:active:not(:disabled) { transform: translateY(0); }
            .ab-btn:disabled { opacity: 0.6; cursor: not-allowed; }
            
            .ab-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
            }
            .ab-table th {
                background-color: #f8f9fa;
                color: #636e72;
                font-weight: 600;
                padding: 12px;
                border-bottom: 2px solid #e1e4e8;
                font-size: 0.9rem;
                text-align: left;
            }
            .ab-table td {
                padding: 12px;
                border-bottom: 1px solid #e1e4e8;
                font-size: 0.85rem;
                vertical-align: middle;
                color: #2d3436;
            }
            .ab-table tr:hover { background-color: #f0fdf9; }
        `}</style>
    );

    return (
        <div style={styles.formWrapper}>
            <PageStyleBlock />
            
            <div style={styles.formCard}>
                <h2 style={styles.formTitle}>Approve Beneficiary List</h2>
                
                {/* Project Info Display (Optional - helps confirm which project is auto-selected) */}
                {!error && projectId && (
                    <div style={{textAlign: 'center', marginBottom: '15px', fontSize: '0.9rem', color: '#636e72'}}>
                        Project ID: <strong>{projectId}</strong> | Dept ID: <strong>{departmentId}</strong>
                    </div>
                )}

                {error && (
                    <div style={{ color: "#e74c3c", marginBottom: "15px", textAlign: 'center', fontWeight: '500', backgroundColor: '#fdecea', padding: '10px', borderRadius: '4px' }}>{error}</div>
                )}
                {message && (
                    <div style={{ color: "#20c997", marginBottom: "15px", textAlign: 'center', fontWeight: '500', backgroundColor: '#e6fffa', padding: '10px', borderRadius: '4px' }}>{message}</div>
                )}

                {isLoading && <p style={{textAlign: 'center', color: '#20c997', fontWeight: '600'}}>Loading beneficiaries...</p>}

                {!isLoading && beneficiaries.length === 0 && !error && (
                    <p style={{textAlign: 'center', color: '#636e72', padding: '20px'}}>
                        No beneficiaries are currently awaiting approval for this project department.
                    </p>
                )}

                {!isLoading && beneficiaries.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="ab-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px', textAlign: 'center' }}>
                                        <input 
                                            type="checkbox" 
                                            className="ab-checkbox"
                                            onChange={handleSelectAll}
                                            checked={isAllSelected}
                                            disabled={!canApproveReject || isLoading}
                                        />
                                    </th>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {beneficiaries.map((b) => (
                                    <tr key={b.beneficiaryId}>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                className="ab-checkbox"
                                                checked={selected.includes(b.beneficiaryId)}
                                                disabled={!canApproveReject || isLoading}
                                                onChange={() => handleCheckboxChange(b.beneficiaryId)}
                                            />
                                        </td>
                                        <td>{b.name}</td>
                                        <td>{b.phone}</td>
                                        <td>
                                            <span style={{ 
                                                backgroundColor: '#fff3cd', 
                                                color: '#856404', 
                                                padding: '4px 8px', 
                                                borderRadius: '4px', 
                                                fontSize: '0.8rem', 
                                                fontWeight: 'bold',
                                                border: '1px solid #ffeeba'
                                            }}>
                                                {b.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {canApproveReject && (
                            <div style={styles.btnGroup}>
                                <button
                                    type="button"
                                    className="ab-btn"
                                    style={styles.primaryBtn}
                                    onClick={() => handleStatusUpdate("active")} 
                                    disabled={isLoading || selected.length === 0}
                                >
                                    {isLoading ? "Processing..." : `Approve Selected (${selected.length})`}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- STYLES (Consistent with Teal Theme) ---
const styles = {
    formWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center', 
        width: '100%',
        minHeight: 'calc(100vh - 80px)', 
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        backgroundColor: '#f0fdf9', // Teal/Mint Background
    },
    formCard: {
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '900px', 
        padding: '30px', 
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        borderTop: '5px solid #20c997', // Teal Border
    },
    formTitle: {
        textAlign: 'center',
        marginBottom: '25px',
        fontSize: '1.5rem',
        color: '#2d3436', // Dark Grey
        fontWeight: '700',
        marginTop: 0,
    },
    btnGroup: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '15px',
        marginTop: '20px',
    },
    primaryBtn: {
        padding: '10px 24px',
        borderRadius: '6px',
        fontSize: '0.9rem',
        fontWeight: '600',
        cursor: 'pointer',
        border: 'none',
        minWidth: '150px',
        transition: 'all 0.2s',
        backgroundColor: '#20c997', // Teal Primary
        color: 'white',
        boxShadow: '0 4px 6px rgba(32, 201, 151, 0.2)',
    },
};

export default ApproveBeneficiaryListPage;